/**
 * Where a declared event goes. Web ships on merge, so the list lives in source next to the event it
 * describes — the server's `analytics_event_descriptors` stays the app's, whose shipped builds
 * cannot be changed.
 */

export const EVENT_INHOUSE = ['trackings', 'amplitude', 'sentry'] as const
export const EVENT_EXTERNAL = ['yandex', 'google', 'facebook'] as const
export const EVENT_PROVIDERS = [...EVENT_INHOUSE, ...EVENT_EXTERNAL] as const

export const PAGEVIEW_INHOUSE = ['amplitude', 'sentry'] as const
export const PAGEVIEW_EXTERNAL = ['yandex', 'google', 'dataLayer'] as const
export const PAGEVIEW_PROVIDERS = [...PAGEVIEW_INHOUSE, ...PAGEVIEW_EXTERNAL] as const

export type AnalyticsEventProvider = (typeof EVENT_PROVIDERS)[number]
export type AnalyticsPageviewProvider = (typeof PAGEVIEW_PROVIDERS)[number]
export type AnalyticsProvider = AnalyticsEventProvider | AnalyticsPageviewProvider

export type ProviderGroups<P extends string> = Readonly<Record<string, readonly P[]>>

/** `external` is ours, not the app's: its descriptors use that word for metrika and firebase. */
export const EVENT_GROUPS = {
  inhouse: EVENT_INHOUSE,
  external: EVENT_EXTERNAL,
  all: EVENT_PROVIDERS,
  default: EVENT_INHOUSE,
} as const satisfies ProviderGroups<AnalyticsEventProvider>

/** A pageview defaults to the external three — `setInhouse` has never been on the one call site. */
export const PAGEVIEW_GROUPS = {
  inhouse: PAGEVIEW_INHOUSE,
  external: PAGEVIEW_EXTERNAL,
  all: PAGEVIEW_PROVIDERS,
  default: PAGEVIEW_EXTERNAL,
} as const satisfies ProviderGroups<AnalyticsPageviewProvider>

export type ProviderToken<P extends string, G extends string> = P | G | `-${P}` | `-${G}`

export type EventProviderToken = ProviderToken<AnalyticsEventProvider, keyof typeof EVENT_GROUPS>
export type PageviewProviderToken = ProviderToken<AnalyticsPageviewProvider, keyof typeof PAGEVIEW_GROUPS>

/**
 * Folds left from the empty set: a bare token adds its members, a `-` token removes them, and the
 * last mention of a provider decides. So `('default', '-yandex', 'yandex')` keeps yandex, and
 * `('yandex', 'amplitude')` means exactly those two rather than the default plus two.
 */
export const resolveProviders = <P extends string>(order: readonly P[], groups: ProviderGroups<P>, tokens: readonly string[]): readonly P[] => {
  const selected = new Set<P>()

  for (const token of tokens) {
    const negated = token.startsWith('-')
    const name = negated ? token.slice(1) : token

    for (const provider of membersOf(order, groups, name)) {
      if (negated) selected.delete(provider)
      else selected.add(provider)
    }
  }

  return order.filter(provider => selected.has(provider))
}

const membersOf = <P extends string>(order: readonly P[], groups: ProviderGroups<P>, name: string): readonly P[] => {
  const group = groups[name]
  if (group) return group
  if ((order as readonly string[]).includes(name)) return [name as P]
  throw new Error(`resolveProviders: unknown provider or group "${name}"`)
}
